/**
 * MUTAGIM.SHOP — Phase 16: Social History
 * Historique des publications
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft, History, ExternalLink, RefreshCw, Instagram,
  Facebook, Video, Calendar, Eye, ThumbsUp, MessageCircle,
  Loader2, Check, AlertCircle
} from 'lucide-react';
import { AdminPageWrapper } from '../components/admin/AdminPageWrapper';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  return localStorage.getItem('admin_token') || localStorage.getItem('auth_token');
}

const PLATFORM_ICONS = {
  instagram: Instagram,
  facebook: Facebook,
  tiktok: Video,
};

const PLATFORM_COLORS = {
  instagram: 'bg-gradient-to-br from-purple-500 to-pink-500',
  facebook: 'bg-blue-600',
  tiktok: 'bg-black',
};

export function AdminSocialHistoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [republishing, setRepublishing] = useState(null);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchHistory();
  }, [navigate]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/social/history?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const republishPost = async (postId, platforms) => {
    setRepublishing(postId);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/social/posts/${postId}/republish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ platforms }),
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.mock) {
          toast.info('Republication simulée (mode MOCK)');
        } else {
          toast.success('Republié avec succès !');
        }
        fetchHistory();
      }
    } catch (error) {
      toast.error('Erreur lors de la republication');
    } finally {
      setRepublishing(null);
    }
  };

  return (
    <AdminPageWrapper title="Historique des Publications" subtitle={`${posts.length} publications`}>
      <div className="space-y-6" data-testid="social-history-page">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-green-400" />
          </div>
        ) : posts.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-12 text-center">
                <History className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Aucune publication</h3>
                <p className="text-white mb-6">
                  Commencez par créer et publier du contenu depuis le Studio
                </p>
                <Button onClick={() => navigate('/admin/social')} className="bg-green-600 hover:bg-green-700">
                  Aller au Studio
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <Card key={post.id} className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Product Image */}
                      {post.product?.image && (
                        <img 
                          src={post.product.image} 
                          alt="" 
                          className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                        />
                      )}

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="text-white font-medium">
                              {post.product?.name?.fr || 'Publication'}
                            </h3>
                            <p className="text-sm text-white line-clamp-2 mt-1">
                              {post.copy_hook}
                            </p>
                          </div>
                          <Badge className="bg-green-500/20 text-green-400 flex-shrink-0">
                            <Check className="w-3 h-3 mr-1" />
                            Publié
                          </Badge>
                        </div>

                        {/* Publication Results */}
                        <div className="mt-4 space-y-2">
                          {post.published_results?.map((result, i) => {
                            const Icon = PLATFORM_ICONS[result.platform] || Calendar;
                            const color = PLATFORM_COLORS[result.platform] || 'bg-gray-600';
                            
                            return (
                              <div 
                                key={i}
                                className="flex items-center gap-3 p-2 bg-zinc-800/50 rounded-lg"
                              >
                                <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", color)}>
                                  <Icon className="w-4 h-4 text-white" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-white text-sm capitalize">{result.platform}</span>
                                    {result.mock && (
                                      <Badge variant="outline" className="text-xs border-yellow-500 text-yellow-500">
                                        MOCK
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-200">
                                    {new Date(result.published_at).toLocaleString('fr-FR')}
                                  </p>
                                </div>
                                
                                {/* Mock stats or external link */}
                                {result.url && (
                                  <a 
                                    href={result.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-white hover:text-white"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Hashtags */}
                        {post.hashtags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {post.hashtags.map((tag, i) => (
                              <span key={i} className="text-xs text-blue-400">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-2 mt-4">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => republishPost(post.id, post.platforms)}
                            disabled={republishing === post.id}
                          >
                            {republishing === post.id ? (
                              <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3 h-3 mr-2" />
                            )}
                            Republier
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => navigate(`/admin/social?post=${post.id}`)}
                          >
                            <Eye className="w-3 h-3 mr-2" />
                            Voir
                          </Button>
                        </div>
                      </div>

                      {/* Date */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm text-white">
                          {new Date(post.published_at || post.created_at).toLocaleDateString('fr-FR')}
                        </p>
                        <p className="text-xs text-gray-200">
                          {new Date(post.published_at || post.created_at).toLocaleTimeString('fr-FR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
      </div>
    </AdminPageWrapper>
  );
}

export default AdminSocialHistoryPage;
